/* eslint-disable @typescript-eslint/no-explicit-any */
// Vendor
import CryptoJS from 'crypto-js';
import aes from 'crypto-js/aes';
import encBase64 from 'crypto-js/enc-base64';
import encUtf8 from 'crypto-js/enc-utf8';
import SHA512 from 'crypto-js/sha512';

// Personal libs
import { IUser, IUserDatabase } from '@shared/interface/model.interface';
import { trim } from './tools.lib';
import { logger } from './logger/logger-manager';
import { IAesEncryptOutput } from '@shared/interface/crypto.interface';
import { UserDto } from '@data/dto/user-dto';

/*****************************************
 *
 * ENCRYPTION / DECRYPTION FUNCTIONS
 *
 *****************************************/
const CryptoJSAesJson = {
  stringify: function (cipherParams) {
    const j = {
      ct: cipherParams.ciphertext.toString(encBase64),
      iv: '',
      s: '',
    };
    if (cipherParams.iv) j.iv = cipherParams.iv.toString();
    if (cipherParams.salt) j.s = cipherParams.salt.toString();
    return JSON.stringify(j);
  },
  parse: function (jsonStr: string) {
    const j = JSON.parse(jsonStr);
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: CryptoJS.enc.Base64.parse(j.ct),
    });
    if (j.iv) cipherParams.iv = CryptoJS.enc.Hex.parse(j.iv);
    if (j.s) cipherParams.salt = CryptoJS.enc.Hex.parse(j.s);
    return cipherParams;
  },
};

/**
 * User encryption
 * @param {Object} texte, key
 * @return {Object} texte
 */
interface IEncrypt {
  texte: string;
  key?: string; // public key (passphrase)
}
export const encrypt = ({
  texte,
  key,
}: IEncrypt): IAesEncryptOutput | undefined => {
  try {
    if (key) {
      const texteToEncrypt = JSON.stringify(texte);

      // Encrypt the texte
      return trim(texteToEncrypt) !== ''
        ? JSON.parse(
            aes
              .encrypt(texteToEncrypt, key, { format: CryptoJSAesJson })
              .toString(),
          )
        : undefined;
    } else {
      logger?.warn('encrypt - warning : the key is undefined');
      return undefined;
    }
  } catch (err: any) {
    logger?.log('encrypt - error : ', err.message);
    return undefined;
  }
};

/**
 * Note decryption
 * @param {Object} texte, User
 * @return {Object} texte
 */
interface IDecrypt {
  texte: IAesEncryptOutput;
  key?: string; // public key (passphrase)
}
export const decrypt = function ({ texte, key }: IDecrypt) {
  try {
    if (key) {
      const texteToDecrypt = JSON.stringify(texte);

      // Decrypt the texte
      return trim(texteToDecrypt) !== ''
        ? JSON.parse(
            aes
              .decrypt(texteToDecrypt, key, { format: CryptoJSAesJson })
              .toString(encUtf8),
          )
        : texteToDecrypt;
    } else {
      logger?.warn('decrypt - warning : the key is undefined');
      return texte;
    }
  } catch (err: any) {
    logger?.log('decrypt - error : ', err.message);
    logger?.log('decrypt - text to decrypt : ', texte);
    return texte;
  }
};

export const encryptUser = (user: IUser): IAesEncryptOutput | undefined => {
  if (user != null && user.uuid != null) {
    const output = encrypt({
      texte: JSON.stringify(user),
      key: user.uuid,
    });

    return output;
  } else {
    return undefined
  }
};

export const decryptUser = (cryptedUser: IUserDatabase): IUser | undefined => {
  if (cryptedUser.data != null && cryptedUser.uuid != null) {
    const decryptedUser = decrypt({
      texte: cryptedUser.data,
      key: cryptedUser.uuid,
    });
    return new UserDto(JSON.parse(decryptedUser));
  } else {
    return undefined;
  }
};

export const decryptUsers = (
  cryptedUsers: Array<IUserDatabase>,
): Array<IUser> => {
  const users: Array<IUser> = [];

  for (const user of cryptedUsers) {
    if (user.data != null && user.uuid != null) {
      const decryptedUser = decrypt({
        texte: user.data,
        key: user.uuid,
      });
      users.push(new UserDto(JSON.parse(decryptedUser)));
    }
  }

  return users;
};

/**
 * Create hash code of readable password (using the email)
 * @param {IHashPassword} params
 * @returns
 */
interface IHashPassword {
  email?: string;
  readablePassword?: string;
}
export const hashPassword = (params: IHashPassword): string =>
  SHA512(`${params.email ?? ''}***${params.readablePassword ?? ''}`).toString();

/**
 * Create hash code of password (using the email)
 * @param {IHashPassword} params
 * @returns
 */
interface IRefreshTokenHash {
  uuid: string;
}
export const hashRefreshToken = (params: IRefreshTokenHash): string =>
  SHA512('***' + params.uuid + '***').toString();
